import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parentalControlsAPI } from '@/lib/api';
import api from '@/lib/api';
import { BrutalButton } from '@/components/brutal';
import { Shield, Mic, Video, ToggleLeft, ToggleRight, ChevronDown, ChevronUp, Save, Music, Image, Volume2, Sparkles, Globe, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import CloneVoiceModal from './CloneVoiceModal';
import VirtualBackgroundsManager from './VirtualBackgroundsManager';
import WalletPurchasesToggle from './WalletPurchasesToggle';
import BeeEntriesToggle from './BeeEntriesToggle';

const STORY_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'zh', label: 'Chinese' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ar', label: 'Arabic' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ru', label: 'Russian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'de', label: 'German' },
  { value: 'ko', label: 'Korean' },
];


const ParentalControlsPanel = ({ studentId, studentName }) => {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [controls, setControls] = useState(null);
  const [forceMedia, setForceMedia] = useState(false);
  const [mediaIntegrationCount, setMediaIntegrationCount] = useState(2);
  const [illustrationsEnabled, setIllustrationsEnabled] = useState(false);
  const [illustrationStyle, setIllustrationStyle] = useState('storybook');
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [ttsVoice, setTtsVoice] = useState('rachel');
  const [storyLanguage, setStoryLanguage] = useState('en');
  const [cloneOpen, setCloneOpen] = useState(false);

  // Live list of voices (curated + this guardian's clones).
  const { data: voiceCatalog, refetch: refetchVoices } = useQuery({
    queryKey: ['tts-voices'],
    queryFn: async () => (await api.get('/tts/voices')).data,
  });
  const clonedVoices = (voiceCatalog?.voices || []).filter((v) => v.kind === 'cloned');

  const deleteVoiceMutation = useMutation({
    mutationFn: (voiceId) => api.delete(`/voices/cloned/${voiceId}`),
    onSuccess: () => {
      toast.success('Voice deleted');
      refetchVoices();
      // If the deleted voice was currently selected, fall back to default
      queryClient.invalidateQueries({ queryKey: ['student', studentId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Could not delete voice'),
  });
  const [chatEnabled, setChatEnabled] = useState(false);
  const [classroomLocked, setClassroomLocked] = useState(false);

  const { data: savedControls } = useQuery({
    queryKey: ['parental-controls', studentId],
    queryFn: async () => (await parentalControlsAPI.get(studentId)).data,
    enabled: !!studentId,
  });

  // Fetch student data for assessment_mode and accessibility_needs
  const { data: studentData } = useQuery({
    queryKey: ['student', studentId],
    queryFn: async () => (await api.get(`/students/${studentId}`)).data,
    enabled: !!studentId,
  });

  useEffect(() => {
    if (savedControls) setControls(savedControls);
  }, [savedControls]);

  useEffect(() => {
    if (studentData) {
      setForceMedia(studentData.force_media_in_stories || false);
      setMediaIntegrationCount(studentData.media_integration_count ?? 2);
      setIllustrationsEnabled(studentData.illustrations_enabled || false);
      setIllustrationStyle(studentData.illustration_style || 'storybook');
      // OFF by default — match the new server-side posture. Old students
      // with explicit tts_enabled=true keep their setting.
      setTtsEnabled(studentData.tts_enabled === true);
      setTtsVoice(studentData.tts_voice || 'rachel');
      setStoryLanguage(studentData.language || 'en');
      setChatEnabled(studentData.chat_enabled || false);
      setClassroomLocked(studentData.classroom_locked || false);
    }
  }, [studentData]);

  const saveMutation = useMutation({
    mutationFn: async (data) => {
      // Save parental controls first
      await parentalControlsAPI.update(studentId, data.controls);
      // Then save student settings (sequential to avoid race on same document)
      await api.patch(`/students/${studentId}`, data.studentSettings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parental-controls', studentId] });
      queryClient.invalidateQueries({ queryKey: ['student', studentId] });
      toast.success(`Reading rules updated for ${studentName}`);
    },
    onError: (error) => {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to save rules: ${detail}`);
      console.error('Reading rules save error:', error.response?.status, error.response?.data || error.message);
    },
  });

  const handleSave = () => {
    if (!controls) return;
    // When recording is required, enforce no skip
    const updatedControls = {
      ...controls,
      can_skip_recording: controls.recording_mode === 'none' ? true : false,
    };
    saveMutation.mutate({
      controls: updatedControls,
      studentSettings: {
        force_media_in_stories: forceMedia,
        media_integration_count: mediaIntegrationCount,
        illustrations_enabled: illustrationsEnabled,
        illustration_style: illustrationStyle,
        tts_enabled: ttsEnabled,
        tts_voice: ttsVoice,
        language: storyLanguage,
        chat_enabled: chatEnabled,
        classroom_locked: classroomLocked,
      },
    });
  };

  if (!controls) return null;

  const Toggle = ({ label, desc, value, onChange, icon: Icon }) => (
    <div className="flex items-start justify-between gap-3 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <div className="flex items-start gap-2.5 flex-1 min-w-0">
        <Icon size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#6366f1' }} />
        <div>
          <p className="text-sm font-bold" style={{ color: '#2d2a26' }}>{label}</p>
          <p className="text-xs mt-0.5" style={{ color: '#8c8780' }}>{desc}</p>
        </div>
      </div>
      <button onClick={() => onChange(!value)} className="flex-shrink-0 mt-0.5"
        data-testid={`toggle-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {value ? (
          <ToggleRight size={28} style={{ color: '#6366f1' }} />
        ) : (
          <ToggleLeft size={28} style={{ color: '#b0aba4' }} />
        )}
      </button>
    </div>
  );

  return (
    <div className="mt-3" data-testid={`parental-controls-${studentId}`}>
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-bold transition-all"
        style={{ background: '#eae6f2', color: '#4338ca', border: '1px solid rgba(99,102,241,0.2)' }}
        data-testid={`expand-controls-${studentId}`}>
        <div className="flex items-center gap-2">
          <Shield size={14} />
          Reading Rules
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {expanded && (
        <div className="mt-2 p-4 rounded-lg" style={{ background: '#f5f3ee', border: '1px solid rgba(0,0,0,0.08)' }}>
          {/* Classroom Lock */}
          <div className="mb-4 p-3 rounded-lg" style={{ background: classroomLocked ? 'rgba(239,68,68,0.08)' : 'rgba(52,211,153,0.08)', border: `1px solid ${classroomLocked ? 'rgba(239,68,68,0.2)' : 'rgba(52,211,153,0.2)'}` }}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                <Shield size={16} className="mt-0.5 flex-shrink-0" style={{ color: classroomLocked ? '#EF4444' : '#10B981' }} />
                <div>
                  <p className="text-sm font-bold" style={{ color: '#2d2a26' }}>
                    {classroomLocked ? 'Classroom Paused — Music Only' : 'Classroom Active — Full Access'}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: '#8c8780' }}>
                    {classroomLocked
                      ? 'Student can only access their music library. Stories, recording, and assessments are hidden.'
                      : 'Student has full access to stories, reading, recording, and all learning features.'}
                  </p>
                </div>
              </div>
              <button onClick={() => setClassroomLocked(!classroomLocked)} className="flex-shrink-0 mt-0.5"
                data-testid="toggle-classroom-lock">
                {classroomLocked ? (
                  <ToggleRight size={28} style={{ color: '#EF4444' }} />
                ) : (
                  <ToggleLeft size={28} style={{ color: '#10B981' }} />
                )}
              </button>
            </div>
          </div>

          {/* Recording Mode */}
          <div className="mb-3">
            <p className="text-xs font-bold uppercase mb-2" style={{ color: '#6366f1' }}>Recording Requirement</p>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { value: 'none', label: 'None', desc: 'No recording required' },
                { value: 'audio_video', label: 'Audio & Video', desc: 'Student reads aloud on camera with microphone' },
                { value: 'audio_only', label: 'Audio Only', desc: 'Student reads aloud with microphone only' },
              ].map(opt => (
                <button key={opt.value}
                  onClick={() => setControls({ ...controls, recording_mode: opt.value })}
                  className="px-3 py-2 rounded-md text-xs font-bold transition-all text-left"
                  style={{
                    background: controls.recording_mode === opt.value ? '#6366f1' : '#eae6f2',
                    color: controls.recording_mode === opt.value ? '#fff' : '#4338ca',
                    border: `1px solid ${controls.recording_mode === opt.value ? '#6366f1' : 'rgba(99,102,241,0.2)'}`,
                  }}
                  data-testid={`recording-mode-${opt.value}`}>
                  {opt.label}
                  <span className="block text-[10px] font-normal mt-0.5" style={{
                    color: controls.recording_mode === opt.value ? 'rgba(255,255,255,0.7)' : '#8c8780',
                  }}>{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Learning Through Songs & Media */}
          <div className="mt-4 mb-3">
            <p className="text-xs font-bold uppercase mb-2" style={{ color: '#6366f1' }}>
              <Music size={14} className="inline mr-1" style={{ verticalAlign: 'middle' }} />
              Learning Through Songs & Media
            </p>
            <div className="flex items-start justify-between gap-3 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                <Music size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#6366f1' }} />
                <div>
                  <p className="text-sm font-bold" style={{ color: '#2d2a26' }}>Force songs/media in every story</p>
                  <p className="text-xs mt-0.5" style={{ color: '#8c8780' }}>When enabled, songs and media will always be included in generated stories</p>
                </div>
              </div>
              <button onClick={() => setForceMedia(!forceMedia)} className="flex-shrink-0 mt-0.5"
                data-testid="toggle-force-media">
                {forceMedia ? (
                  <ToggleRight size={28} style={{ color: '#6366f1' }} />
                ) : (
                  <ToggleLeft size={28} style={{ color: '#b0aba4' }} />
                )}
              </button>
            </div>
            <div className="py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="flex items-start gap-2.5">
                <Music size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#6366f1' }} />
                <div className="flex-1">
                  <p className="text-sm font-bold" style={{ color: '#2d2a26' }}>Number of song/media integrations per story</p>
                  <p className="text-xs mt-0.5" style={{ color: '#8c8780' }}>
                    How many songs or media references to include (1-5)
                  </p>
                  <input type="number" min="1" max="5" value={mediaIntegrationCount}
                    onChange={(e) => setMediaIntegrationCount(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
                    className="mt-2 w-20 px-3 py-1.5 rounded-md text-sm font-bold text-center"
                    style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.15)', color: '#2d2a26' }}
                    data-testid="media-integration-count-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Story Language */}
          <div className="mt-4 mb-3">
            <p className="text-xs font-bold uppercase mb-2" style={{ color: '#6366f1' }}>
              <Globe size={14} className="inline mr-1" style={{ verticalAlign: 'middle' }} />
              Story Language
            </p>
            <p className="text-xs mb-2" style={{ color: '#8c8780' }}>
              Choose the language for generated stories
            </p>
            <select
              value={storyLanguage}
              onChange={(e) => setStoryLanguage(e.target.value)}
              className="reading-rules-select w-full px-3 py-2 rounded-md text-sm font-bold"
              data-testid="story-language-select"
            >
              {STORY_LANGUAGE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Story Enhancements */}
          <div className="mt-4 mb-3">
            <p className="text-xs font-bold uppercase mb-2" style={{ color: '#6366f1' }}>
              <Sparkles size={14} className="inline mr-1" style={{ verticalAlign: 'middle' }} />
              Story Enhancements
            </p>

            {/* Enable AI Illustrations */}
            <div className="flex items-start justify-between gap-3 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                <Image size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#6366f1' }} />
                <div>
                  <p className="text-sm font-bold" style={{ color: '#2d2a26' }}>Enable AI Illustrations</p>
                  <p className="text-xs mt-0.5" style={{ color: '#8c8780' }}>Generate visual scene descriptions for each chapter</p>
                </div>
              </div>
              <button onClick={() => setIllustrationsEnabled(!illustrationsEnabled)} className="flex-shrink-0 mt-0.5"
                data-testid="toggle-illustrations">
                {illustrationsEnabled ? (
                  <ToggleRight size={28} style={{ color: '#6366f1' }} />
                ) : (
                  <ToggleLeft size={28} style={{ color: '#b0aba4' }} />
                )}
              </button>
            </div>

            {/* Illustration Style */}
            {illustrationsEnabled && (
              <div className="py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <div className="flex items-start gap-2.5">
                  <Image size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#6366f1' }} />
                  <div className="flex-1">
                    <p className="text-sm font-bold" style={{ color: '#2d2a26' }}>Illustration Style</p>
                    <p className="text-xs mt-0.5" style={{ color: '#8c8780' }}>Choose the visual style for story illustrations</p>
                    <div className="grid grid-cols-3 gap-1.5 mt-2">
                      {[
                        { value: 'watercolor', label: 'Watercolor' },
                        { value: 'cartoon', label: 'Cartoon' },
                        { value: 'realistic', label: 'Realistic' },
                        { value: 'storybook', label: 'Storybook' },
                        { value: 'anime', label: 'Anime' },
                      ].map(opt => (
                        <button key={opt.value}
                          onClick={() => setIllustrationStyle(opt.value)}
                          className="px-3 py-2 rounded-md text-xs font-bold transition-all text-center"
                          style={{
                            background: illustrationStyle === opt.value ? '#6366f1' : '#eae6f2',
                            color: illustrationStyle === opt.value ? '#fff' : '#4338ca',
                            border: `1px solid ${illustrationStyle === opt.value ? '#6366f1' : 'rgba(99,102,241,0.2)'}`,
                          }}
                          data-testid={`illustration-style-${opt.value}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Enable Read-Aloud (TTS) — off by default. Audio narration
                is reserved for kids who genuinely need it (English-as-a-
                second-language learners, dyslexia, low vision). For native
                English speakers, listening replaces reading and undermines
                the core skill the program is building. */}
            <div className="flex items-start justify-between gap-3 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                <Volume2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#6366f1' }} />
                <div>
                  <p className="text-sm font-bold" style={{ color: '#2d2a26' }}>Enable Read-Aloud (Text-to-Speech)</p>
                  <p className="text-xs mt-0.5" style={{ color: '#8c8780' }}>
                    <strong>Recommended OFF for most students.</strong> Independent reading is the curriculum.
                    Turn this on for English-as-a-second-language learners or kids with audio-dependent
                    accessibility needs (dyslexia, low vision). When new students are added,
                    this is auto-enabled only when their language isn&apos;t English or their accessibility
                    profile signals an audio need.
                  </p>
                </div>
              </div>
              <button onClick={() => setTtsEnabled(!ttsEnabled)} className="flex-shrink-0 mt-0.5"
                data-testid="toggle-tts">
                {ttsEnabled ? (
                  <ToggleRight size={28} style={{ color: '#6366f1' }} />
                ) : (
                  <ToggleLeft size={28} style={{ color: '#b0aba4' }} />
                )}
              </button>
            </div>

            {/* Voice picker — works across all story languages via ElevenLabs multilingual */}
            {ttsEnabled && (
              <div className="py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <div className="flex items-start gap-2.5 mb-2">
                  <Volume2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#6366f1' }} />
                  <div>
                    <p className="text-sm font-bold" style={{ color: '#2d2a26' }}>Reader Voice</p>
                    <p className="text-xs mt-0.5" style={{ color: '#8c8780' }}>The same voice reads in every language your child's stories are generated in.</p>
                  </div>
                </div>
                <select
                  value={ttsVoice}
                  onChange={(e) => setTtsVoice(e.target.value)}
                  data-testid="tts-voice-select"
                  className="reading-rules-select w-full mt-1 px-3 py-2 rounded-md text-sm"
                >
                  {(voiceCatalog?.voices || [
                    { key: 'rachel', label: 'Rachel — warm female narrator', kind: 'curated' },
                    { key: 'lily', label: 'Lily — young, gentle female', kind: 'curated' },
                    { key: 'sarah', label: 'Sarah — soft female', kind: 'curated' },
                    { key: 'matilda', label: 'Matilda — friendly female', kind: 'curated' },
                    { key: 'george', label: 'George — warm British male', kind: 'curated' },
                    { key: 'daniel', label: 'Daniel — calm British male', kind: 'curated' },
                  ]).map((v) => (
                    <option key={v.key} value={v.key}>{v.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Custom Voices (parent-cloned) */}
            {ttsEnabled && (
              <div className="py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                <div className="flex items-start justify-between gap-2.5 mb-2">
                  <div className="flex items-start gap-2.5">
                    <Mic size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#6366f1' }} />
                    <div>
                      <p className="text-sm font-bold" style={{ color: '#2d2a26' }}>Your Cloned Voices</p>
                      <p className="text-xs mt-0.5" style={{ color: '#8c8780' }}>Record your own voice (or grandma's, or anyone you have consent for) and stories can be read in it.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setCloneOpen(true)}
                    className="flex items-center gap-1 px-3 py-1 rounded-md text-xs font-bold flex-shrink-0"
                    style={{ background: '#6366f1', color: '#fff' }}
                    data-testid="open-clone-voice-modal"
                  >
                    <Plus size={12} /> Clone a Voice
                  </button>
                </div>
                {clonedVoices.length === 0 ? (
                  <p className="text-xs italic mt-1" style={{ color: '#a39b87' }}>No cloned voices yet.</p>
                ) : (
                  <div className="space-y-1 mt-2">
                    {clonedVoices.map((v) => {
                      const inUse = ttsVoice === v.key;
                      return (
                        <div key={v.key} className="flex items-center justify-between gap-2 px-2 py-1 rounded-md"
                          style={{ background: inUse ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.6)', border: '1px solid rgba(0,0,0,0.08)' }}>
                          <span className="text-xs font-bold" style={{ color: '#2d2a26' }}>{v.label}</span>
                          <div className="flex items-center gap-1">
                            {!inUse && (
                              <button onClick={() => setTtsVoice(v.key)}
                                className="text-xs font-bold px-2 py-1 rounded"
                                style={{ background: '#6366f1', color: '#fff' }}
                                data-testid={`use-voice-${v.key}`}>
                                Use
                              </button>
                            )}
                            {inUse && (
                              <span className="text-[10px] font-bold uppercase px-2 py-1 rounded"
                                style={{ background: 'rgba(99,102,241,0.18)', color: '#4338ca' }}>In use</span>
                            )}
                            <button
                              onClick={() => {
                                if (window.confirm(`Delete "${v.label}"? This removes it from your account and from the voice provider.`)) {
                                  deleteVoiceMutation.mutate(v.key);
                                }
                              }}
                              disabled={deleteVoiceMutation.isPending}
                              className="p-1 rounded hover:bg-rose-50 disabled:opacity-50"
                              data-testid={`delete-voice-${v.key}`}
                              title="Delete this voice">
                              <Trash2 size={12} style={{ color: '#e11d48' }} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <CloneVoiceModal
            open={cloneOpen}
            onClose={() => setCloneOpen(false)}
            onCreated={() => refetchVoices()}
          />

          {/* Song purchases from the parent's wallet */}
          <WalletPurchasesToggle studentId={studentId} studentName={studentName} />
          <BeeEntriesToggle studentId={studentId} studentName={studentName} />

          {/* Wishful Video Backgrounds */}
          <VirtualBackgroundsManager
            studentId={studentId}
            studentName={studentName}
            interests={studentData?.interests || []}
          />

          {/* Classroom Chat */}
          <div className="mt-4 mb-3">
            <p className="text-xs font-bold uppercase mb-2" style={{ color: '#6366f1' }}>
              Communication
            </p>
            <div className="flex items-start justify-between gap-3 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <div className="flex items-start gap-2.5 flex-1 min-w-0">
                <Shield size={16} className="mt-0.5 flex-shrink-0" style={{ color: '#6366f1' }} />
                <div>
                  <p className="text-sm font-bold" style={{ color: '#2d2a26' }}>Allow Classroom Chat</p>
                  <p className="text-xs mt-0.5" style={{ color: '#8c8780' }}>Let your child send and receive messages with classmates. Teacher and admin must also enable chat.</p>
                </div>
              </div>
              <button onClick={() => setChatEnabled(!chatEnabled)} className="flex-shrink-0 mt-0.5"
                data-testid="toggle-chat">
                {chatEnabled ? (
                  <ToggleRight size={28} style={{ color: '#6366f1' }} />
                ) : (
                  <ToggleLeft size={28} style={{ color: '#b0aba4' }} />
                )}
              </button>
            </div>
          </div>

          <div className="mt-4">
            <BrutalButton variant="indigo" size="sm" fullWidth onClick={handleSave}
              disabled={saveMutation.isPending}
              className="flex items-center justify-center gap-2"
              data-testid={`save-controls-${studentId}`}>
              <Save size={14} />
              {saveMutation.isPending ? 'Saving...' : 'Save Reading Rules'}
            </BrutalButton>
          </div>
        </div>
      )}

    </div>
  );
};

export default ParentalControlsPanel;
